import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { kr } from "../../lib/labels";

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const icon = (selected, price) => L.divIcon({
  className: "",
  html: `<div style="background:${selected ? "#F0A94C" : "#087F72"};color:#fff;border:2px solid #fff;border-radius:999px;padding:${price ? "3px 8px" : "0"};min-width:22px;height:22px;display:flex;align-items:center;justify-content:center;font:600 11px Inter,sans-serif;box-shadow:0 2px 6px rgba(0,0,0,.25);white-space:nowrap">${price ? `${esc(kr(price))} kr.` : ""}</div>`,
  iconSize: null,
  iconAnchor: [11, 11],
});
const DK_CENTER = [56.1, 10.6];

// OpenStreetMap + Leaflet map with clustering; marker prices only for verified, comparable fixed prices
export const ResultsMap = ({ markers, selectedId, onSelect, center }) => {
  const el = useRef(null);
  const map = useRef(null);
  const layer = useRef(null);
  const refs = useRef({});
  useEffect(() => {
    map.current = L.map(el.current, { scrollWheelZoom: false }).setView(DK_CENTER, 6);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 18, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>-bidragydere' }).addTo(map.current);
    layer.current = L.markerClusterGroup({ showCoverageOnHover: false, maxClusterRadius: 45 });
    map.current.addLayer(layer.current);
    return () => { map.current.remove(); map.current = null; };
  }, []);
  useEffect(() => {
    layer.current.clearLayers();
    refs.current = {};
    markers.forEach((m) => {
      const mk = L.marker([m.lat, m.lng], { icon: icon(m.location_id === selectedId, m.price_label), title: m.clinic_name, keyboard: true });
      mk.bindPopup(`<strong>${esc(m.clinic_name)}</strong><br/><a href="/dyreklinikker/${encodeURIComponent(m.clinic_slug)}">Se klinikprofil</a>`);
      mk.on("click", () => onSelect(m.location_id));
      layer.current.addLayer(mk);
      refs.current[m.location_id] = mk;
    });
    const pts = markers.map((m) => [m.lat, m.lng]);
    map.current.invalidateSize();
    if (pts.length) map.current.fitBounds(L.latLngBounds(center ? [...pts, [center.lat, center.lng]] : pts), { padding: [30, 30], maxZoom: 13 });
    else if (center) map.current.setView([center.lat, center.lng], 11);
    else map.current.setView(DK_CENTER, 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers, center]);
  useEffect(() => {
    Object.entries(refs.current).forEach(([id, mk]) => {
      const m = markers.find((x) => x.location_id === id);
      mk.setIcon(icon(id === selectedId, m?.price_label));
    });
    const sel = refs.current[selectedId];
    if (sel) sel.setZIndexOffset(1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);
  return <div ref={el} className="h-full w-full rounded-2xl overflow-hidden border border-[#D7E7E3] z-0" role="region" aria-label="Kort over klinikker" data-testid="results-map" />;
};
